import Async from "components/DesignSystem/AsyncComponents/Async";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import Wind from "components/icons/Wind";
import { BULK_INVOICE_ACTION, BULK_INVOICE_TYPE } from "constants/invoicing";
import { useInvoiceBulkAction } from "hooks/useInvoiceBulkAction";
import { useToast } from "hooks/useToast";
import { useMemo, useState } from "react";
import { RowSelectionState } from "react-table-8.10.7";
import { useBulkUpdateInvoiceMutation } from "store/apis/invoices";
import { BackendError } from "types/utils/error";
import { pluralize } from "utils/pluralize";
import { InvoiceTable } from "./InvoiceTable";

export default function InvoiceMarkAsActiveModal({
  groupId,
  entityId,
  selectedRows,
  rowSelection,
  onSuccess,
}: {
  groupId: string;
  entityId: string;
  selectedRows: string[];
  rowSelection?: RowSelectionState;
  onSuccess: () => void;
}) {
  const [bulkUpdateOrDeleteInvoice, { isLoading: isMarkingAsActive }] =
    useBulkUpdateInvoiceMutation();
  const { alertToast, successToast } = useToast();

  const [rowSelectionState, setRowSelectionState] = useState(
    rowSelection || {}
  );

  const newSelectedRows = useMemo(
    () =>
      Object.entries(rowSelectionState)
        .filter(([_, isSelected]) => isSelected)
        .map(([uuid]) => uuid),
    [rowSelectionState]
  );

  const {
    allInvoices,
    isLoading,
    valid,
    invalid,
    totalInvoices,
    isAllInvoiceInvalid,
    shouldShowButton,
  } = useInvoiceBulkAction({
    groupId,
    entityId,
    selectedRows,
    newSelectedRows: newSelectedRows,
  });

  const onMarkAsActive = async (rowIds?: string[]) => {
    try {
      if (rowIds?.length === 0) return;
      await bulkUpdateOrDeleteInvoice({
        entityId: entityId!,
        groupId: groupId!,
        record_ids: rowIds ?? [],
        action: BULK_INVOICE_ACTION.MARK_AS_ACTIVE,
        type: BULK_INVOICE_TYPE.INVOICE,
      }).unwrap();
      onSuccess();
      successToast({
        message: `Selected invoices marked as active.`,
      });
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  return (
    <>
      <Modal.Header>
        <Modal.Title>Mark invoices as active</Modal.Title>
        <Modal.Close />
      </Modal.Header>
      <Modal.Body>
        <Async.Root
          isLoading={isLoading}
          isSuccess={!isLoading}
          isEmpty={false}
        >
          <Async.Success>
            {newSelectedRows.length > 0 && (
              <>
                {isAllInvoiceInvalid && (
                  <div className="t-flex t-items-center t-gap-2 t-flex-col t-pb-12">
                    <Wind className="t-text-neutral-40" />
                    The{" "}
                    {pluralize(
                      newSelectedRows.length,
                      "invoice is",
                      "invoices are"
                    )}{" "}
                    already active and hence cannot be updated.
                  </div>
                )}

                {valid.length > 0 && (
                  <>
                    <div className="t-mb-4">
                      Are you sure you want mark{" "}
                      <b>
                        {valid.length} out of {totalInvoices} invoices{" "}
                      </b>{" "}
                      as active?
                    </div>
                    {invalid.length > 0 && (
                      <div className="t-mb-4">
                        <Banner
                          type="warning"
                          description={
                            <span>
                              The remaining{" "}
                              {pluralize(
                                invalid.length,
                                "invoice is",
                                "invoices are"
                              )}{" "}
                              already active and hence can not be updated.
                            </span>
                          }
                        />
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            <InvoiceTable
              invoicesData={allInvoices?.invoices}
              onRowClick={() => () => {}}
              disabledRowIds={invalid.map((row) => row?.uuid)}
              showActionsColumn={false}
              rowSelection={rowSelectionState}
              setRowSelection={setRowSelectionState}
            />
          </Async.Success>
        </Async.Root>
      </Modal.Body>
      <Modal.FooterButtonGroup>
        <Modal.RawClose asChild>
          <Button>Cancel</Button>
        </Modal.RawClose>
        {shouldShowButton && (
          <Button
            customType="primary"
            onClick={() => onMarkAsActive(valid.map((row) => row.uuid))}
            isLoading={isMarkingAsActive}
            disabled={isMarkingAsActive}
          >
            Mark as Active
          </Button>
        )}
      </Modal.FooterButtonGroup>
    </>
  );
}
